import { useEffect, useState } from 'react'

import clsx from 'clsx'
import { Outlet, useLoaderData, useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router'

import { BrandingBar } from '~/portal/components/BrandingBar'
import ConsumerHeadTags from '~/portal/components/ConsumerHeadTags'
import { TopBar } from '~/portal/components/ConsumerTopBar'
import { CustomCssPreviewBanner } from '~/portal/components/CustomCssPreviewBanner'
import { PreviewTopBar } from '~/portal/components/PreviewTopBar'
import { useConsumerBranding } from '~/portal/hooks/useConsumerBranding'
import { useEntitlements } from '~/portal/hooks/useEntitlements'
import { useProducts } from '~/portal/hooks/useProducts'
import { useUser } from '~/portal/hooks/useUser'
import { loader as formLoader } from '~/portal/routes/_docs/loader'
import { PortalRouteHandle } from '~/portal/types/app'
import { LayoutType } from '~/portal/types/layout'
import { ConsumerLoaderData, RootOutletContext } from '~/portal/types/portal'
import { PreviewMode } from '~/portal/types/preview'
import { CUSTOM_CSS_PREVIEW_QUERY_PARAM, CUSTOM_CSS_PREVIEW_QUERY_PARAM_VALUE, CUSTOM_CSS_PREVIEW_SESSION_MARKER } from '~/portal/utils/shared/customCssPreview'
import { ErrorBoundaryComponent } from '~/shared/components/ErrorBoundary'

export const loader = formLoader

const buildBrandingCss = (accentLight?: string, accentDark?: string): string => {
  const light = accentLight ?? '#0077C4'
  const dark = accentDark ?? light
  return (
    `body.sp-consumer{--sp-color-accent:${light}}` +
    `.dark body.sp-consumer{--sp-color-accent:${dark}}`
  )
}

export const handle: PortalRouteHandle = {
  bodyClass: 'sp-consumer',
  layout: {
    [LayoutType.DEFAULT]: {
      previewTopBar: {
        productContent: {
          disabled: false
        }
      }
    }
  }
}

export default function ConsumerAppRootPage() {
  const { previewMode, gtmKey, customDomainActive, platformAdmin } = useLoaderData<ConsumerLoaderData>()
  const { previewData, isPreview: isLandingPagePreview } = useOutletContext<RootOutletContext>() || {}
  const navigate = useNavigate()
  const { sbFooterRemoved, customDomainEnabled } = useEntitlements()
  const [searchParams] = useSearchParams()

  const { pathname, hash } = useLocation()
  const isLandingPage = pathname === '/'

  const isCssPreview = searchParams.get(CUSTOM_CSS_PREVIEW_QUERY_PARAM) === CUSTOM_CSS_PREVIEW_QUERY_PARAM_VALUE
  const isCssPreviewSession = isCssPreview || (typeof window !== 'undefined' && sessionStorage.getItem(CUSTOM_CSS_PREVIEW_SESSION_MARKER) === '1')

  const skipPreview = isLandingPage && !!previewMode && !previewData

  const isPreview = isLandingPagePreview || !!previewMode

  const { favicon, logoImage, accentColor, accentColorLight, accentColorDark, fontName, name, customCss, hasCustomCss } = useConsumerBranding()

  useEffect(() => {
    if (isCssPreview) {
      sessionStorage.setItem(CUSTOM_CSS_PREVIEW_SESSION_MARKER, '1')
    }
  }, [isCssPreview])

  const { user } = useUser()

  const { currentProduct, products } = useProducts()
  const [sideNavOpen, setSideNavOpen] = useState(false)
  const [instantSideNavClose, setInstantSideNavClose] = useState(false)

  const toggleSideNavWithTransition = (toggle) => {
    setSideNavOpen(toggle)
  }

  const closeSideNavWithoutTransition = () => {
    setInstantSideNavClose(true)
    setSideNavOpen(false)
  }

  useEffect(() => {
    setInstantSideNavClose(false)
    setSideNavOpen(false)
  }, [pathname, hash])

  useEffect(() => {
    // on landing page user should not see preview if there are no preview data
    // (this is the case when direct link for landing page preview is used)
    if (skipPreview) {
      navigate('/')
    }
  }, [])

  if (skipPreview) { // do not render, navigation happens
    return null
  }

  return (
    <section className="flex flex-col w-full h-full">
      <ConsumerHeadTags 
        favicon={favicon} 
        previewName={isPreview ? name : ''} 
        googleTagManager={customDomainEnabled && customDomainActive && gtmKey ? gtmKey : undefined}
      />
      {!hasCustomCss && <style dangerouslySetInnerHTML={{ __html: buildBrandingCss(accentColorLight, accentColorDark) }} />}
      {hasCustomCss && customCss && <style dangerouslySetInnerHTML={{ __html: customCss }} />}
      <div className={clsx('sp-custom-css-scope sp-app contents', hasCustomCss && 'sp-custom-css-active')}>
        {isCssPreviewSession && <CustomCssPreviewBanner />}
        {isPreview && <PreviewTopBar mode={previewMode as PreviewMode} />}
        <TopBar
          logo={logoImage}
          accentColor={accentColor}
          fontFamily={fontName}
          user={user}
          name={name}
          currentProduct={currentProduct}
          disabled={isPreview}
          hideStripes={hasCustomCss}
          toggleSideNavWithTransition={toggleSideNavWithTransition}
          products={products}
          platformAdmin={platformAdmin}
        />
        <Outlet
          context={{ isPreview, previewData, sideNavOpen, instantSideNavClose, toggleSideNavWithTransition, closeSideNavWithoutTransition } satisfies RootOutletContext}
        />
        {!sbFooterRemoved && <BrandingBar />}
      </div>
    </section>)
}

export const ErrorBoundary = ErrorBoundaryComponent